import React, { useState, useEffect, useRef } from "react";
import { get, post } from "../../services/api";
import { useSearchParams, useNavigate } from "react-router-dom"; // Add this import
import { toast } from "react-toastify";

const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

// Age calculation function
const calculateAge = (dobString) => {
  if (!dobString) return "";

  try {
    const dob = new Date(dobString);
    const today = new Date();

    // Check if date is valid
    if (isNaN(dob.getTime())) return "";

    let years = today.getFullYear() - dob.getFullYear();
    let months = today.getMonth() - dob.getMonth();
    let days = today.getDate() - dob.getDate();

    // Adjust if current month/day is before birth month/day
    if (days < 0) {
      months--;
      // Get days in previous month
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    if (years > 0) {
      return `${years} year${years > 1 ? "s" : ""}`;
    } else if (months > 0) {
      return `${months} month${months > 1 ? "s" : ""}`;
    } else {
      return `${days} day${days > 1 ? "s" : ""}`;
    }
  } catch (error) {
    console.error("Error calculating age:", error);
    return "";
  }
};

const StudentInquiry = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [cnic, setCnic] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [registeredStudents, setRegisteredStudents] = useState([]);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [isStudentsExpanded, setIsStudentsExpanded] = useState(false);
  const [isParentExpanded, setIsParentExpanded] = useState(true);
  const [isStudentDetailsExpanded, setIsStudentDetailsExpanded] =
    useState(true);
  const [isManualFeeExpanded, setIsManualFeeExpanded] = useState(true);
  const [isAttachmentsExpanded, setIsAttachmentsExpanded] = useState(false);
  const centerId = localStorage.getItem("centerId");
  const organizationId = localStorage.getItem("organizationId");
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const roleId = localStorage.getItem("roleId");
  const [isFamilyExisting, setIsFamilyExisting] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null); // Track which student is being edited
  const [familyInfo, setFamilyInfo] = useState({
    fullName: "",
    relationship: "",
    cnic: "",
    phoneNumber: "",
    primaryContactMobile: "",
    email: "",
    job: "",
    employerName: "",
    countryId: "",
    stateId: "",
    cityId: "",
    currentAddress: "",
    permanentAddress: "",
    mailingAddress: "",
  });

  const [familyErrors, setFamilyErrors] = useState({
    fullName: "",
    relationship: "",
    cnic: "",
    primaryContactMobile: "",
    email: "",
  });

  const [students, setStudents] = useState([
    {
      studentFullName: "",
      dob: "",
      gender: "",
      language: "",
      classId: "",
      className: "",
      applyingFor: "",
      entryTerm: "",
      concessionTypeId: "",
      feeElements: [],
      printDiscountedOnChallan: false,
      concessionApplyManually: false,
    },
  ]);
  const handleEditClick = (registrationId) => {
    const studentToEdit = registeredStudents.find(
      (s) => s.registrationId === registrationId,
    );

    if (!studentToEdit) return;

    // Set this student as the one being edited
    setSelectedStudentId(registrationId);
    let selectedConcessionOption = null;
    if (studentToEdit.centerConcessionId) {
      for (const group of concessionData) {
        const found = group.options.find(
          (opt) => opt.value === studentToEdit.centerConcessionId,
        );
        if (found) {
          selectedConcessionOption = found;
          break;
        }
      }
    }
    // Populate the students array with ONLY this student for editing
    const editedStudent = {
      studentFullName: studentToEdit.studentFullName,
      dob: studentToEdit.dob ? studentToEdit.dob.split("T")[0] : "",
      gender: studentToEdit.gender,
      language:
        homeLanguage.find((l) => l.id === studentToEdit.languageId)?.value ||
        "",
      classId: studentToEdit.classId,
      className: studentToEdit.className,
      applyingFor: studentToEdit.sessionName || "",
      entryTerm: studentToEdit.entryTermId || "",
      concessionTypeId: studentToEdit.centerConcessionId || "",
      registrationId: studentToEdit.registrationId,
      concessionOption: selectedConcessionOption,
      feeElements: studentToEdit.feeElements || [],
      printDiscountedOnChallan: studentToEdit.printDiscountedOnChallan || false,
      concessionApplyManually: studentToEdit.concessionApplyManually || false,
    };

    setStudents([editedStudent]);
    setAttachments([[]]);
    
    // Fetch mapped fee plan if they already have one associated with class
    if (studentToEdit.classId) {
      setTimeout(() => {
        fetchFeePlan(studentToEdit.classId, 0, studentToEdit.registrationId);
      }, 0);
    }

    setIsStudentDetailsExpanded(true);
    setIsAttachmentsExpanded(true);

    // Scroll to student details
    setTimeout(() => {
      const studentDetailsSection = document.getElementById(
        "student-details-section",
      );
      if (studentDetailsSection) {
        studentDetailsSection.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        const firstInput = document.getElementById("student-0-studentFullName");
        if (firstInput) {
          firstInput.focus();
        }
      }
    }, 100);
  };
  const [studentErrors, setStudentErrors] = useState([
    {
      studentFullName: "",
      dob: "",
      gender: "",
      classId: "",
      applyingFor: "",
      entryTerm: "",
    },
  ]);

  const [attachments, setAttachments] = useState([[]]); // Changed to array of arrays for dynamic files per student

  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [homeLanguage, setHomeLanguage] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [genders, setGenders] = useState([]);
  const [concessionData, setConcessionData] = useState([]);

  const fetchedClasses = useRef(false);
  const fetchedSessions = useRef(false);
  const fetchedTerms = useRef(false);
  const fetchedHomeLanguage = useRef(false);
  const fetchedCountries = useRef(false);
  const fetchedRelationships = useRef(false);
  const fetchedGenders = useRef(false);

  // Custom Hook to manage file input refs
  const useFileInputRefs = (count) => {
    const [refs, setRefs] = useState([]);

    useEffect(() => {
      setRefs(
        Array(count)
          .fill()
          .map(() => React.createRef()),
      );
    }, [count]);

    const resetFileInput = (index) => {
      if (refs[index]?.current) {
        refs[index].current.value = null;
      }
    };

    return { refs, resetFileInput };
  };

  // Use the custom hook
  const { refs: fileInputRefs, resetFileInput } = useFileInputRefs(
    students.length,
  );
  useEffect(() => {
    const cnicFromUrl = searchParams.get("cnic");
    if (cnicFromUrl) {
      const formattedCnic = formatCnic(cnicFromUrl.replace(/\D/g, ""));
      setCnic(formattedCnic);

      // Trigger search automatically
      if (formattedCnic.match(/^\d{5}-\d{7}-\d{1}$/)) {
        handleSearch(formattedCnic);
      }
    }
  }, [searchParams]);
  // Show notification function
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: "", message: "" });
    }, 5000);
  };

  // Print functionality
  const handlePrint = () => {
    const printContent = document.getElementById("printable-content");
    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent.outerHTML;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  // Handle file uploads
  const handleFileChange = (index, file) => {
    try {
      validateFileSize(file);
      const newAttachments = [...attachments];
      newAttachments[index] = [...(newAttachments[index] || []), file];
      setAttachments(newAttachments);
      resetFileInput(index); // Reset file input after successful upload
    } catch (error) {
      showNotification("error", error.message);
    }
  };

  // Remove file
  const handleRemoveFile = (studentIndex, fileIndex) => {
    const newAttachments = [...attachments];
    newAttachments[studentIndex] = newAttachments[studentIndex].filter(
      (_, idx) => idx !== fileIndex,
    );
    setAttachments(newAttachments);
  };

  const handleSearch = async (e) => {
    let cnicToSearch;

    if (e && typeof e === "string") {
      
      cnicToSearch = e;
      setCnic(e);
    } else if (e && e.target) {
      
      e.preventDefault();
      cnicToSearch = cnic; 
    } else {
      cnicToSearch = cnic; 
    }

 
    if (
      cnicToSearch &&
      cnicToSearch.match &&
      cnicToSearch.match(/^\d{5}-\d{7}-\d{1}$/)
    ) {
      setError("");
      await fetchStudentsByCnic(cnicToSearch);
      setShowResults(true);
    } else {
      setError("Please enter a valid CNIC (e.g., 12345-6789012-3).");
    }
  };

  // Format CNIC input
  const formatCnic = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 5) {
      return numbers;
    } else if (numbers.length <= 12) {
      return `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
    } else {
      return `${numbers.slice(0, 5)}-${numbers.slice(5, 12)}-${numbers.slice(
        12,
        13,
      )}`;
    }
  };

  const handleCnicChange = (e) => {
    const formattedCnic = formatCnic(e.target.value);
    setCnic(formattedCnic);
    // Reset form fields for new CNIC
    setFamilyInfo({
      fullName: "",
      relationship: "",
      cnic: formattedCnic,
      phoneNumber: "",
      primaryContactMobile: "",
      email: "",
      job: "",
      employerName: "",
      countryId: "",
      stateId: "",
      cityId: "",
      currentAddress: "",
      permanentAddress: "",
      mailingAddress: "",
    });
    setRegisteredStudents([]);
    setIsFamilyExisting(false);
    setShowResults(false);
  };

  // Format Mobile input
  const formatMobile = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 4) {
      return numbers;
    } else {
      return `${numbers.slice(0, 4)}-${numbers.slice(4, 11)}`;
    }
  };

  const handleMobileChange = (e) => {
    const formattedMobile = formatMobile(e.target.value);
    setFamilyInfo({ ...familyInfo, primaryContactMobile: formattedMobile });
  };

  const fetchClasses = async () => {
    if (fetchedClasses.current) return;
    fetchedClasses.current = true;
    try {
      setLoading(true);
      const response = await get(
        `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setClasses(response || []);
    } catch (err) {
      setError("An error occurred while fetching classes.");
      fetchedClasses.current = false;
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    if (fetchedSessions.current) return;
    fetchedSessions.current = true;
    try {
      setLoading(true);
      const response = await get(
        `FeePlan/GetCenterSessionsByCenterId?centerId=${centerId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const sessionsData = response.centerSessions || [];
      setSessions(sessionsData);

      // Automatically select first session for all students
      if (sessionsData.length > 0) {
        setStudents((prevStudents) =>
          prevStudents.map((student) => ({
            ...student,
            applyingFor: sessionsData[0].sessionName,
          })),
        );
      }
    } catch (err) {
      setError("An error occurred while fetching sessions.");
      fetchedSessions.current = false;
    } finally {
      setLoading(false);
    }
  };

  const fetchTerms = async () => {
    if (fetchedTerms.current) return;
    fetchedTerms.current = true;
    try {
      setLoading(true);
      const response = await get(
        `StudentEvaluation/GetAllEvaluationCriteriaType?OrganizationId=${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setTerms(response?.evaluationTypes || []);
    } catch (err) {
      setError("An error occurred while fetching terms.");
      fetchedTerms.current = false;
    } finally {
      setLoading(false);
    }
  };

  const fetchHomeLanguage = async () => {
    if (fetchedHomeLanguage.current) return;
    fetchedHomeLanguage.current = true;
    try {
      setLoading(true);
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=homeLanguage`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setHomeLanguage(response?.commonSettingDetail || []);
    } catch (err) {
      setError("An error occurred while fetching home language options.");
      fetchedHomeLanguage.current = false;
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    if (fetchedCountries.current) return;
    fetchedCountries.current = true;
    try {
      setLoading(true);
      const response = await get(
        `InspStudentFamilyRegistrations/dropdowns/countries`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setCountries(response || []);
    } catch (err) {
      setError("An error occurred while fetching countries.");
      fetchedCountries.current = false;
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async (countryId) => {
    try {
      setLoading(true);
      const response = await get(
        `InspStudentFamilyRegistrations/dropdowns/states?countryId=${countryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setStates(response || []);
    } catch (err) {
      setError("An error occurred while fetching states.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async (stateId) => {
    try {
      setLoading(true);
      const response = await get(
        `InspStudentFamilyRegistrations/dropdowns/cities?stateId=${stateId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setCities(response || []);
    } catch (err) {
      setError("An error occurred while fetching cities.");
    } finally {
      setLoading(false);
    }
  };

  const fetchRelationships = async () => {
    if (fetchedRelationships.current) return;
    fetchedRelationships.current = true;
    try {
      setLoading(true);
      const response = await get(
        `InspStudentFamilyRegistrations/dropdowns/relationships`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setRelationships(response || []);
    } catch (err) {
      setError("An error occurred while fetching relationships.");
      fetchedRelationships.current = false;
    } finally {
      setLoading(false);
    }
  };

  const fetchGenders = async () => {
    if (fetchedGenders.current) return;
    fetchedGenders.current = true;
    try {
      setLoading(true);
      const response = await get(
        `InspStudentFirstRegistrtion/dropdowns/genders`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setGenders(response || []);
    } catch (err) {
      setError("An error occurred while fetching genders.");
      fetchedGenders.current = false;
    } finally {
      setLoading(false);
    }
  };
  const fetchConcession = async () => {
    try {
      setLoading(true);
      const response = await get(
        `ConcessionType/GetCenterConcessionByCenterId?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const groupedOptions = response.concession.map((group) => ({
        label: group.label,
        options: group.concessionDiscounts.map((concession) => ({
          value: concession.concessionTypeId, // Map id to value
          label: concession.concessionTypeName, // Map name to label
        })),
      }));
      setConcessionData(groupedOptions);
    } catch (err) {
      setError("An error occurred while fetching concession.");
    } finally {
      setLoading(false);
    }
  };

  const fetchFeePlan = async (classId, studentIndex, regId = 0) => {
    if (!classId) return;
    try {
      setLoading(true);
      const url = regId 
        ? `FeePlan/GetFeePlanCenterClassId?centerId=${centerId}&centerClassId=${classId}&regId=${regId}`
        : `FeePlan/GetFeePlanCenterClassId?centerId=${centerId}&centerClassId=${classId}`;
      const response = await get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response && response.isSuccess) {
        setStudents((prev) => {
          const newStudents = [...prev];
          newStudents[studentIndex].feeElements = response.classes.map(fee => {
            const defaultAmt = fee.defaultAmount || 0;
            // API returns chargebaleAmount
            const apiChargeableAmt = fee.chargeableAmount !== undefined && fee.chargeableAmount !== null ? fee.chargeableAmount : fee.chargebaleAmount;
            
            const chargeableAmt = apiChargeableAmt !== undefined && apiChargeableAmt !== null ? apiChargeableAmt : defaultAmt;
            const discountAmt = fee.discount !== undefined && fee.discount !== null ? fee.discount : (defaultAmt - chargeableAmt);
            
            // Safely parse boolean to avoid string "false" causing truthy values
            let apiPrintOnChallan = fee.printDefaultFeeOnChallan;
            if (apiPrintOnChallan === "false") apiPrintOnChallan = false;
            if (apiPrintOnChallan === "true") apiPrintOnChallan = true;

            const finalPrintDefault = apiPrintOnChallan !== undefined && apiPrintOnChallan !== null 
                ? !!apiPrintOnChallan 
                : (discountAmt <= 0);

            return {
              ...fee,
              printDefaultFeeOnChallan: finalPrintDefault,
              id: fee.id || 0,
              feeElementId: fee.feeElementId || 0,
              chargebaleAmount: chargeableAmt,
              discount: discountAmt
            };
          });
          return newStudents;
        });
      }
    } catch (err) {
      console.error("Error fetching fee plan:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChargeableAmountChange = (e, studentIndex, feeIndex) => {
    const valStr = e.target.value;
    const val = valStr === "" ? "" : parseFloat(valStr);
    const newStudents = [...students];
    const fee = newStudents[studentIndex].feeElements[feeIndex];
    fee.chargebaleAmount = val;
    const discount = (fee.defaultAmount || 0) - (val === "" ? 0 : val);
    fee.discount = discount;
    
    // Automatically uncheck if there is a discount greater than zero
    if (discount > 0) {
      fee.printDefaultFeeOnChallan = false;
    } else {
      fee.printDefaultFeeOnChallan = true;
    }
    setStudents(newStudents);
  };

  const handlePrintDefaultFeeChange = (e, studentIndex, feeIndex) => {
    const { checked } = e.target;
    const newStudents = [...students];
    newStudents[studentIndex].feeElements[feeIndex].printDefaultFeeOnChallan = checked;
    setStudents(newStudents);
  };

  const handleConcessionChange = (e, studentIndex) => {
    const value = e.target.value;
    const newStudents = [...students];
    newStudents[studentIndex].concessionTypeId = value;

    // Find the full option object for concessionOption
    let selectedOption = null;
    if (value) {
      for (const group of concessionData) {
        const found = group.options.find(
          (opt) => String(opt.value) === String(value),
        );
        if (found) {
          selectedOption = found;
          break;
        }
      }
    }
    newStudents[studentIndex].concessionOption = selectedOption;
    setStudents(newStudents);
  };
  const fetchStudentsByCnic = async (cnic) => {
    if (!cnic) {
      setError("CNIC is required to fetch student data.");
      return;
    }
    try {
      setLoading(true);
      const data = await get(
        `InspStudentFamilyRegistrations/GetAllStudentsByCnic?cnic=${cnic}&centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setIsFamilyExisting(!!data?.familyWithStudentsDto?.familyInfo?.id);
      if (data.isSuccess && data.familyWithStudentsDto?.familyInfo) {
        setFamilyInfo({
          ...data.familyWithStudentsDto.familyInfo,
          cnic,
          currentAddress:
            data.familyWithStudentsDto.familyInfo.currentAddress || "",
          permanentAddress:
            data.familyWithStudentsDto.familyInfo.permanentAddress || "",
          mailingAddress:
            data.familyWithStudentsDto.familyInfo.mailingAddress || "",
        });
      } else {
        setFamilyInfo({
          fullName: "",
          relationship: "",
          cnic,
          phoneNumber: "",
          primaryContactMobile: "",
          email: "",
          job: "",
          employerName: "",
          countryId: "",
          stateId: "",
          cityId: "",
          currentAddress: "",
          permanentAddress: "",
          mailingAddress: "",
        });
      }
      const mappedStudents =
        data.familyWithStudentsDto?.students?.map((student) => ({
          ...student,
          gender: student.gender,
          language:
            homeLanguage.find((l) => l.id === student.languageId)?.value || "",
          classId: student.classId.toString(),
          className: student.className,
          applyingFor: student.sessionName || "", // Preselect 2025-2026 for fetched students
          entryTerm: student.entryTerm || "",
          feeElements: (student.feeElements || []).map(fee => {
            const defaultAmt = fee.defaultAmount || 0;
            const apiChargeableAmt = fee.chargeableAmount !== undefined && fee.chargeableAmount !== null ? fee.chargeableAmount : fee.chargebaleAmount;
            
            const chargeableAmt = apiChargeableAmt !== undefined && apiChargeableAmt !== null ? apiChargeableAmt : defaultAmt;
            const discountAmt = fee.discount !== undefined && fee.discount !== null ? fee.discount : (defaultAmt - chargeableAmt);

            // Safely parse boolean to avoid string "false" causing truthy values
            let apiPrintOnChallan = fee.printDefaultFeeOnChallan;
            if (apiPrintOnChallan === "false") apiPrintOnChallan = false;
            if (apiPrintOnChallan === "true") apiPrintOnChallan = true;

            const finalPrintDefault = apiPrintOnChallan !== undefined && apiPrintOnChallan !== null 
                ? !!apiPrintOnChallan 
                : (discountAmt <= 0);

            return {
              ...fee,
              chargebaleAmount: chargeableAmt,
              discount: discountAmt,
              printDefaultFeeOnChallan: finalPrintDefault,
              id: fee.id || 0,
              feeElementId: fee.feeElementId || 0,
            };
          }),
          printDiscountedOnChallan: student.printDiscountedOnChallan || false,
          concessionApplyManually: student.concessionApplyManually || false,
        })) || [];
      setRegisteredStudents(mappedStudents);
      setError("");
    } catch (err) {
      setError("An error occurred while fetching data.");
      setFamilyInfo({
        fullName: "",
        relationship: "",
        cnic,
        phoneNumber: "",
        primaryContactMobile: "",
        email: "",
        job: "",
        employerName: "",
        countryId: "",
        stateId: "",
        cityId: "",
        currentAddress: "",
        permanentAddress: "",
        mailingAddress: "",
      });
      setRegisteredStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    let hasError = false;
    let firstErrorId = null;

    // Phone number validation regex
    const phoneRegex = /^03[0-4][0-9]-[0-9]{7}$/;
    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Clear errors
    setFamilyErrors({
      fullName: "",
      relationship: "",
      cnic: "",
      primaryContactMobile: "",
      email: "",
    });
    const newStudentErrors = students.map(() => ({
      studentFullName: "",
      dob: "",
      gender: "",
      classId: "",
      applyingFor: "",
      entryTerm: "",
    }));

    // Validate familyInfo
    const newFamilyErrors = {};
    if (!familyInfo.fullName) {
      newFamilyErrors.fullName = "Required";
      hasError = true;
      if (!firstErrorId) firstErrorId = "family-fullName";
    }
    if (!familyInfo.relationship) {
      newFamilyErrors.relationship = "Required";
      hasError = true;
      if (!firstErrorId) firstErrorId = "family-relationship";
    }
    if (!familyInfo.cnic) {
      newFamilyErrors.cnic = "Required";
      hasError = true;
      if (!firstErrorId) firstErrorId = "family-cnic";
    }
    if (!familyInfo.primaryContactMobile) {
      newFamilyErrors.primaryContactMobile = "Required";
      hasError = true;
      if (!firstErrorId) firstErrorId = "family-primaryContactMobile";
    } else if (!phoneRegex.test(familyInfo.primaryContactMobile)) {
      newFamilyErrors.primaryContactMobile =
        "Invalid format (e.g., 03XX-XXXXXXX)";
      hasError = true;
      if (!firstErrorId) firstErrorId = "family-primaryContactMobile";
    }
    if (familyInfo.email && !emailRegex.test(familyInfo.email)) {
      newFamilyErrors.email = "Invalid email format";
      hasError = true;
      if (!firstErrorId) firstErrorId = "family-email";
    }

    // Validate students
    students.forEach((student, index) => {
      if (!student.studentFullName) {
        newStudentErrors[index].studentFullName = "Required";
        hasError = true;
        if (!firstErrorId) firstErrorId = `student-${index}-studentFullName`;
      }
      if (!student.dob) {
        newStudentErrors[index].dob = "Required";
        hasError = true;
        if (!firstErrorId) firstErrorId = `student-${index}-dob`;
      } else {
        const selectedDate = new Date(student.dob);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate > today) {
          newStudentErrors[index].dob = "Future dates are not allowed";
          hasError = true;
          if (!firstErrorId) firstErrorId = `student-${index}-dob`;
        }
      }
      if (!student.gender) {
        newStudentErrors[index].gender = "Required";
        hasError = true;
        if (!firstErrorId) firstErrorId = `student-${index}-gender`;
      }
      if (!student.classId) {
        newStudentErrors[index].classId = "Required";
        hasError = true;
        if (!firstErrorId) firstErrorId = `student-${index}-classId`;
      }
      if (!student.applyingFor) {
        newStudentErrors[index].applyingFor = "Required";
        hasError = true;
        if (!firstErrorId) firstErrorId = `student-${index}-applyingFor`;
      }
      if (!student.entryTerm) {
        newStudentErrors[index].entryTerm = "Required";
        hasError = true;
        if (!firstErrorId) firstErrorId = `student-${index}-entryTerm`;
      }
    });

    setFamilyErrors(newFamilyErrors);
    setStudentErrors(newStudentErrors);

    if (hasError) {
      if (firstErrorId) {
        const elem = document.getElementById(firstErrorId);
        if (elem) {
          elem.focus();
          elem.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      return;
    }

    try {
      setLoading(true);
      const currentTime = new Date().toISOString();
      const studentIds = [];
      let familyId;

      // Check if family already exists by fetching data with CNIC
      const familyResponse = await get(
        `InspStudentFamilyRegistrations/GetAllStudentsByCnic?cnic=${familyInfo.cnic}&centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (
        familyResponse.isSuccess &&
        familyResponse.familyWithStudentsDto?.familyInfo?.id > 0
      ) {
        // Use existing family ID
        familyId = familyResponse.familyWithStudentsDto.familyInfo.id;
      } else {
        // Save new family record
        const familyPayload = {
          id: 0,
          centerId: parseInt(centerId),
          fullName: familyInfo.fullName,
          organizationId: organizationId,
          phoneNumber: familyInfo.phoneNumber,
          primaryContactMobile: familyInfo.primaryContactMobile,
          email: familyInfo.email,
          cnic: familyInfo.cnic,
          relationship: familyInfo.relationship,
          job: familyInfo.job,
          employerName: familyInfo.employerName,
          countryId: parseInt(familyInfo.countryId) || 1,
          stateId: parseInt(familyInfo.stateId) || 0,
          cityId: parseInt(familyInfo.cityId) || 0,
          currentAddress: familyInfo.currentAddress,
          permanentAddress: familyInfo.permanentAddress,
          mailingAddress: familyInfo.mailingAddress,
          isDelete: false,
          createdOn: currentTime,
          createdBy: userId || "system",
          updatedOn: currentTime,
          updatedBy: userId || "system",
        };

        // Save new family record
        const familySaveResponse = await post(
          "InspStudentFamilyRegistrations",
          familyPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!familySaveResponse.isSuccess) {
          showNotification(
            "error",
            familySaveResponse.message || "Failed to save family data.",
          );
          return;
        } else {
          toast.info(familySaveResponse.message, {
            position: "top-center",
            autoClose: 5000,
            style: { minWidth: "500px" },
          });
        }
        familyId = familySaveResponse.data.id;
      }

      // Save each student with the family ID
      for (let i = 0; i < students.length; i++) {
        const student = students[i];
        const studentAttachments = attachments[i] || [];

        // Prepare attachments array for API
        const attachmentsArray = [];

        // Process each file and convert to base64
        for (const file of studentAttachments) {
          try {
            const fileContent = await readFileAsBase64(file);
            attachmentsArray.push({
              fileName: file.name,
              fileContent: fileContent,
            });
          } catch (error) {
            console.error(`Error reading file ${file.name}:`, error);
          }
        }

        // Find session ID for "2025-2026", fallback to "2024-2025" or first session
        const session = sessions.find((s) => s.name === student.applyingFor) ||
          sessions.find((s) => s.name === "2024-2025") ||
          sessions[0] || { id: 3 };
        const isUpdate = !!student.registrationId;
        const studentPayload = {
          id: isUpdate ? student.registrationId : 0,
          inspStudentFamilyInfoId: familyId,
          applyingForSession: session.id,
          classId: parseInt(student.classId),
          centerConcessionId: parseInt(student.concessionTypeId),
          entryTermId: parseInt(student.entryTerm),
          studentFullName: student.studentFullName,
          dob: student.dob ? new Date(student.dob).toISOString() : currentTime,
          genderId: genders.find((g) => g.name === student.gender)?.id || 1,
          languageId:
            homeLanguage.find((l) => l.value === student.language)?.id || 1,
          centerId: parseInt(centerId),
          code: "", // Empty string as per API requirement
          isDelete: false,
          createdOn: currentTime,
          createdBy: userId || "system",
          updatedOn: currentTime,
          updatedBy: userId || "system",
          attachments: attachmentsArray,
          guardianIdentityNo: familyInfo.cnic || "",
          isDataImported: false,
          feeDetails: student.feeElements.map(fee => ({
            id: fee.id || 0,
            studentFirstRegistrationId: isUpdate ? student.registrationId : 0,
            feeElementId: fee.feeElementId || 0,
            feeElement: fee.feeElement || "",
            printDefaultFeeOnChallan: fee.printDefaultFeeOnChallan || false,
            defaultAmount: fee.defaultAmount || 0,
            chargeableAmount: fee.chargebaleAmount || 0,
            discount: fee.discount !== undefined ? fee.discount : (fee.defaultAmount - (fee.chargebaleAmount || 0))
          }))
        };
        const studentResponse = await post(
          "InspStudentFirstRegistrtion/Create",
          studentPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (studentResponse.isSuccess) {
          studentIds.push(studentResponse.data.id);
        } else {
          showNotification(
            "error",
            studentResponse.message || "Failed to save student data.",
          );
          return;
        }
      }

      setCnic(familyInfo.cnic);
      setError("");
      showNotification("success", "Data saved successfully!");
      const defaultSessionName =
        sessions.length > 0 ? sessions[0].sessionName : "";
      
      const hasNewStudent = students.some((s) => !s.registrationId);

      // Handle automatic voucher printing for Center 2076 & New Registrations
      if (centerId === "2076" && hasNewStudent) {
        for (let i = 0; i < studentIds.length; i++) {
           const student = students[i];
           if (!student.registrationId) { // Only print for new students
              const studentId = studentIds[i];
              const session = sessions.find((s) => s.name === student.applyingFor) || sessions.find((s) => s.name === "2024-2025") || sessions[0] || { id: 3 };
              
              try {
                const feePlanResponse = await get(
                  `Student/GenerateAdmissionChallan?FirstRegistrationId=${studentId}&createdBy=${userId || "system"}&centerSessionId=${session.id}`,
                  {
                    headers: { Authorization: `Bearer ${token}` }
                  }
                );

                if (feePlanResponse?.isSuccess && feePlanResponse?.centerFeePlans?.length > 0) {
                  const feeItems = feePlanResponse.centerFeePlans;
                  const totalAmount = feeItems.reduce((sum, item) => sum + item.amount, 0);
                  
                  const formatDate = (dateString) => {
                    const date = new Date(dateString);
                    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
                  };
                  
                  const issueDate = formatDate(feeItems[0].issueDate);
                  const dueDate = formatDate(feeItems[0].dueDate);

                  const customVoucherData = {
                    acNo: feeItems[0]?.code || "",
                    className: feeItems[0]?.className || student.className || "N/A",
                    feeMonth: feeItems.find(k => k.feeTypeName.toLowerCase().includes("admission"))?.feeTypeName || feeItems[0]?.feeTypeName || issueDate,
                    studentName: student.studentFullName,
                    fatherName: feeItems[0]?.fatherName || familyInfo.fullName,
                    contact: "0345-7586677",
                    dueDate: dueDate,
                    totalDue: totalAmount.toFixed(0),
                    totalAfter: (totalAmount + 100).toFixed(0),
                    admissionFee: feeItems.find(k => k.feeTypeName.toLowerCase().includes("admission"))?.amount.toFixed(0) || "0",
                    tutionFee: feeItems.find(k => k.feeTypeName.toLowerCase().includes("tution") || k.feeTypeName.toLowerCase().includes("tuition"))?.amount.toFixed(0) || "0",
                    regFee: feeItems.find(k => k.feeTypeName.toLowerCase().includes("registration"))?.amount.toFixed(0) || "0",
                    balance: "0",
                    annualStationery: feeItems.find(k => k.feeTypeName.toLowerCase().includes("annual"))?.amount.toFixed(0) || "0",
                    acCharges: feeItems.find(k => k.feeTypeName.toLowerCase().includes("ac charges"))?.amount.toFixed(0) || "0",
                    sportsFee: feeItems.find(k => k.feeTypeName.toLowerCase().includes("sports"))?.amount.toFixed(0) || "0",
                    others: feeItems.filter(k => !["admission", "tution", "tuition", "registration", "ac charges", "sports", "annual"].some(t => k.feeTypeName.toLowerCase().includes(t)))
                                     .reduce((sum, k) => sum + k.amount, 0).toFixed(0),
                    stationery: "0",
                    fine: "0",
                    paperFund: "0",
                    transportFee: "0",
                    other: "0",
                    admissionVoucher: feeItems[0]?.challanNo,
                    centerImagePath: feeItems[0]?.centerImagePath ? `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${feeItems[0].centerImagePath}` : "",
                  };

                  localStorage.setItem('customVoucherData', JSON.stringify(customVoucherData));
                  window.open('/admission-voucher', '_blank');
                }
              } catch (printError) {
                 console.error("Auto-print challan failed:", printError);
              }
           }
        }
      }

      setStudents([
        {
          studentFullName: "",
          dob: "",
          gender: "",
          language: "",
          classId: "",
          className: "",
          entryTerm: "",
          concessionTypeId: "",
          concessionOption: null,
          applyingFor: defaultSessionName,
          feeElements: [],
          printDiscountedOnChallan: false,
          concessionApplyManually: false,
        },
      ]);
      setAttachments([[]]);
      await fetchStudentsByCnic(cnic);
      
      // Only redirect if this was a new registration 
      if (hasNewStudent) {
        setTimeout(() => {
          navigate("/registrations");
        }, 2000);
      }
    } catch (err) {
      console.error("Error saving data:", err);
      showNotification("error", "An error occurred while saving data.");
    } finally {
      setLoading(false);
    }
  };

  const validateFileSize = (file, maxSizeInMB = 5) => {
    const maxSize = maxSizeInMB * 1024 * 1024;
    if (file && file.size > maxSize) {
      throw new Error(`File size should be less than ${maxSizeInMB}MB`);
    }
    return true;
  };

  useEffect(() => {
    if (centerId) {
      fetchClasses();
      fetchSessions();
      fetchTerms();
      fetchHomeLanguage();
      fetchCountries();
      fetchRelationships();
      fetchGenders();
    }
    fetchConcession();
  }, [centerId]);

  useEffect(() => {
    if (familyInfo.countryId) {
      fetchStates(familyInfo.countryId);
    }
  }, [familyInfo.countryId]);

  useEffect(() => {
    if (familyInfo.stateId) {
      fetchCities(familyInfo.stateId);
    }
  }, [familyInfo.stateId]);

  return (
    <div className="" id="printable-content">
      {notification.show && (
        <div
          className={`alert ${
            notification.type === "success" ? "alert-success" : "alert-danger"
          } alert-dismissible fade show position-fixed`}
          style={{
            top: "20px",
            right: "20px",
            zIndex: 9999,
            minWidth: "300px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
          role="alert"
        >
          <div className="d-flex align-items-center">
            <i
              className={`fas ${
                notification.type === "success"
                  ? "fa-check-circle"
                  : "fa-exclamation-circle"
              } me-2`}
            ></i>
            <strong className="me-2">
              {notification.type === "success" ? "Success!" : "Error!"}
            </strong>
            {notification.message}
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={() =>
              setNotification({ show: false, type: "", message: "" })
            }
            style={{ fontSize: "12px" }}
          ></button>
        </div>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div className="text-center my-3">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="row mb-5">
        <div className="col-md-6 offset-md-3">
          <div className="text-center mb-3">
            <h4 className="fw-bold text-primary">Student Inquiry</h4>
            <p className="text-muted">
              Search for student records by CNIC number
            </p>
          </div>
          <div className="input-group">
            <input
              type="text"
              className="form-control border-primary rounded-pill py-2 px-4"
              style={{ paddingRight: "40px" }}
              placeholder="Enter CNIC (e.g., 12345-6789012-3)"
              pattern="\d{5}-\d{7}-\d{1}"
              value={cnic}
              onChange={handleCnicChange}
              onKeyPress={(e) => e.key === "Enter" && handleSearch(e)}
              maxLength="15"
            />
            <span
              className="input-group-text border-0 bg-transparent"
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                zIndex: 10,
              }}
              onClick={(e) => handleSearch(e)} 
            >
              <i
                className="fas fa-search text-primary"
                style={{ filter: "none" }}
              ></i>
            </span>
          </div>
          <div className="form-text text-center mt-2">
            CNIC format: 5 digits - 7 digits - 1 digit (e.g., 12345-6789012-3)
          </div>
          {error && <div className="text-danger text-center mt-2">{error}</div>}
        </div>
      </div>

      {showResults && (
        <div className="row">
          {/* Registered Students Table (Only show if records exist) */}
          {registeredStudents.length > 0 && (
            <div className="col-12 mb-4">
              <div
                className="card shadow-sm border-0"
                style={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "1px solid #eef2f7",
                }}
              >
                <div
                  className="card-header d-flex justify-content-between align-items-center py-3"
                  style={{
                    backgroundColor: "#f8fafc",
                    borderBottom: "1px solid #eef2f7",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "10px",
                        backgroundColor: "#3b82f6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "12px",
                      }}
                    >
                      <i
                        className="fas fa-users text-white"
                        style={{ fontSize: "18px" }}
                      ></i>
                    </div>
                    <div>
                      <h5
                        className="mb-0 fw-semibold"
                        style={{ color: "#1e293b" }}
                      >
                        Registered Students
                      </h5>
                      <small className="text-muted">
                        {registeredStudents.length} student
                        {registeredStudents.length !== 1 ? "s" : ""} found
                      </small>
                    </div>
                  </div>
                  {registeredStudents.length > 5 && (
                    <button
                      className="btn btn-outline-primary btn-sm d-flex align-items-center"
                      onClick={() => setIsStudentsExpanded(!isStudentsExpanded)}
                      style={{
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        padding: "6px 12px",
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                    >
                      <i
                        className={`fas fa-${
                          isStudentsExpanded ? "chevron-up" : "chevron-down"
                        } me-2`}
                        style={{ fontSize: "11px" }}
                      ></i>
                      {isStudentsExpanded ? "Show Less" : "Show More"}
                    </button>
                  )}
                </div>

                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table
                      className="table table-hover m-0"
                      style={{ borderCollapse: "separate", borderSpacing: 0 }}
                    >
                      <thead style={{ backgroundColor: "#f8fafc" }}>
                        <tr>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "left",
                            }}
                          >
                            #
                          </th>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "left",
                            }}
                          >
                            Reg No
                          </th>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "left",
                            }}
                          >
                            Name
                          </th>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "left",
                            }}
                          >
                            DOB
                          </th>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "left",
                            }}
                          >
                            Gender
                          </th>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "left",
                            }}
                          >
                            Class
                          </th>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "left",
                            }}
                          >
                            Session
                          </th>
                          <th
                            style={{
                              padding: "14px 16px",
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: "1px solid #eef2f7",
                              textAlign: "center",
                            }}
                          >
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(isStudentsExpanded
                          ? registeredStudents
                          : registeredStudents.slice(0, 5)
                        ).map((student, index) => (
                          <tr
                            key={index}
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#ffffff" : "#fafbfc",
                              borderBottom: "1px solid #eef2f7",
                              transition: "background-color 0.2s ease",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "#f1f5f9")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                index % 2 === 0 ? "#ffffff" : "#fafbfc")
                            }
                          >
                            <td
                              style={{
                                padding: "14px 16px",
                                fontWeight: 500,
                                color: "#475569",
                                fontSize: "14px",
                              }}
                            >
                              {index + 1}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                fontWeight: 600,
                                color: "#1e293b",
                                fontSize: "14px",
                              }}
                            >
                              <span
                                style={{
                                  backgroundColor: "#f1f5f9",
                                  padding: "4px 8px",
                                  borderRadius: "6px",
                                  fontSize: "13px",
                                  fontFamily: "monospace",
                                }}
                              >
                                {student.registrationId}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                fontWeight: 500,
                                color: "#1e293b",
                                fontSize: "14px",
                              }}
                            >
                              {student.studentFullName}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                color: "#64748b",
                                fontSize: "14px",
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <i
                                  className="fas fa-calendar-alt me-2"
                                  style={{ fontSize: "12px", color: "#94a3b8" }}
                                ></i>
                                {new Date(student.dob).toLocaleDateString(
                                  "en-GB",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  },
                                )}
                              </div>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <span
                                style={{
                                  padding: "4px 10px",
                                  borderRadius: "20px",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  backgroundColor:
                                    student.gender === "Male"
                                      ? "#dbeafe"
                                      : "#fce7f3",
                                  color:
                                    student.gender === "Male"
                                      ? "#1d4ed8"
                                      : "#be185d",
                                }}
                              >
                                {student.gender}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                fontWeight: 500,
                                color: "#475569",
                                fontSize: "14px",
                              }}
                            >
                              {student.className}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                color: "#64748b",
                                fontSize: "14px",
                              }}
                            >
                              <span
                                style={{
                                  padding: "4px 8px",
                                  backgroundColor: "#f0fdf4",
                                  color: "#166534",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                }}
                              >
                                {student.sessionName}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                              }}
                            >
                              <button
                                className="btn btn-sm"
                                onClick={() =>
                                  handleEditClick(student.registrationId)
                                }
                                title="Edit Student"
                                style={{
                                  backgroundColor: "transparent",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "8px",
                                  padding: "6px 12px",
                                  color: "#3b82f6",
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#3b82f6";
                                  e.currentTarget.style.color = "white";
                                  e.currentTarget.style.borderColor = "#3b82f6";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                  e.currentTarget.style.color = "#3b82f6";
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                                }}
                              >
                                <i
                                  className="fas fa-pencil-alt me-1"
                                  style={{ fontSize: "12px" }}
                                ></i>
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Show More/Less Indicator */}
                    {registeredStudents.length > 5 && !isStudentsExpanded && (
                      <div
                        className="text-center py-3"
                        style={{
                          borderTop: "1px solid #eef2f7",
                          backgroundColor: "#f8fafc",
                        }}
                      >
                        <span
                          className="text-muted"
                          style={{ fontSize: "13px" }}
                        >
                          Showing 5 of {registeredStudents.length} students •
                          <button
                            className="btn btn-link p-0 ms-1"
                            onClick={() => setIsStudentsExpanded(true)}
                            style={{
                              textDecoration: "none",
                              color: "#3b82f6",
                              fontWeight: 500,
                            }}
                          >
                            Show all
                          </button>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Parent Information Card - Compact Design */}
          <div className="col-12 mb-4">
            <div
              className="card shadow-sm border-0"
              style={{
                borderRadius: "10px",
                overflow: "hidden",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                className="card-header d-flex justify-content-between align-items-center py-2 px-3"
                style={{
                  backgroundColor: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      backgroundColor: "#8b5cf6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "10px",
                    }}
                  >
                    <i
                      className="fas fa-user-circle text-white"
                      style={{ fontSize: "14px" }}
                    ></i>
                  </div>
                  <div>
                    <h5
                      className="mb-0 fw-semibold"
                      style={{ color: "#1e293b", fontSize: "15px" }}
                    >
                      Parent Information
                    </h5>
                    <small className="text-muted" style={{ fontSize: "11px" }}>
                      Primary contact details for the family
                    </small>
                  </div>
                </div>
                <button
                  className="btn btn-outline-primary btn-sm d-flex align-items-center"
                  onClick={() => setIsParentExpanded(!isParentExpanded)}
                  style={{
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    padding: "4px 10px",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  <i
                    className={`fas fa-${
                      isParentExpanded ? "chevron-up" : "chevron-down"
                    } me-1`}
                    style={{ fontSize: "10px" }}
                  ></i>
                  {isParentExpanded ? "Collapse" : "Expand"}
                </button>
              </div>

              {isParentExpanded && (
                <div className="card-body" style={{ padding: "16px" }}>
                  <div className="row g-3">
                    <div className="col-md-6 col-lg-4">
                      <div className="mb-2">
                        <label
                          className="form-label fw-medium mb-1"
                          style={{ color: "#475569", fontSize: "13px" }}
                        >
                          Full Name <span className="text-danger">*</span>
                        </label>
                        <input
                          id="family-fullName"
                          type="text"
                          className={`form-control ${
                            familyErrors.fullName ? "is-invalid" : ""
                          }`}
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            backgroundColor: isFamilyExisting
                              ? "#f8fafc"
                              : "#ffffff",
                          }}
                          value={familyInfo.fullName}
                          onChange={(e) =>
                            setFamilyInfo({
                              ...familyInfo,
                              fullName: e.target.value,
                            })
                          }
                          placeholder="Enter Full Name"
                          disabled={isFamilyExisting}
                        />
                        {familyErrors.fullName && (
                          <div
                            className="invalid-feedback"
                            style={{ fontSize: "11px" }}
                          >
                            {familyErrors.fullName}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="mb-2">
                        <label
                          className="form-label fw-medium mb-1"
                          style={{ color: "#475569", fontSize: "13px" }}
                        >
                          Relationship <span className="text-danger">*</span>
                        </label>
                        <select
                          id="family-relationship"
                          className={`form-select ${
                            familyErrors.relationship ? "is-invalid" : ""
                          }`}
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            backgroundColor: isFamilyExisting
                              ? "#f8fafc"
                              : "#ffffff",
                          }}
                          value={familyInfo.relationship}
                          onChange={(e) =>
                            setFamilyInfo({
                              ...familyInfo,
                              relationship: e.target.value,
                            })
                          }
                          disabled={isFamilyExisting}
                        >
                          <option value="">Select Relationship</option>
                          {relationships.map((rel) => (
                            <option key={rel.id} value={rel.name}>
                              {rel.name}
                            </option>
                          ))}
                        </select>
                        {familyErrors.relationship && (
                          <div
                            className="invalid-feedback"
                            style={{ fontSize: "11px" }}
                          >
                            {familyErrors.relationship}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="mb-2">
                        <label
                          className="form-label fw-medium mb-1"
                          style={{ color: "#475569", fontSize: "13px" }}
                        >
                          CNIC <span className="text-danger">*</span>
                        </label>
                        <input
                          id="family-cnic"
                          type="text"
                          className={`form-control ${
                            familyErrors.cnic ? "is-invalid" : ""
                          }`}
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            backgroundColor: "#f8fafc",
                          }}
                          value={familyInfo.cnic}
                          disabled
                          placeholder="Enter CNIC"
                          pattern="\d{5}-\d{7}-\d{1}"
                          maxLength="15"
                        />
                        {familyErrors.cnic && (
                          <div
                            className="invalid-feedback"
                            style={{ fontSize: "11px" }}
                          >
                            {familyErrors.cnic}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="mb-2">
                        <label
                          className="form-label fw-medium mb-1"
                          style={{ color: "#475569", fontSize: "13px" }}
                        >
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            backgroundColor: isFamilyExisting
                              ? "#f8fafc"
                              : "#ffffff",
                          }}
                          value={familyInfo.phoneNumber}
                          onChange={(e) =>
                            setFamilyInfo({
                              ...familyInfo,
                              phoneNumber: e.target.value,
                            })
                          }
                          placeholder="Enter Phone Number"
                          disabled={isFamilyExisting}
                        />
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="mb-2">
                        <label
                          className="form-label fw-medium mb-1"
                          style={{ color: "#475569", fontSize: "13px" }}
                        >
                          Mobile <span className="text-danger">*</span>
                        </label>
                        <input
                          id="family-primaryContactMobile"
                          type="tel"
                          className={`form-control ${
                            familyErrors.primaryContactMobile
                              ? "is-invalid"
                              : ""
                          }`}
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            backgroundColor: isFamilyExisting
                              ? "#f8fafc"
                              : "#ffffff",
                          }}
                          value={familyInfo.primaryContactMobile}
                          onChange={handleMobileChange}
                          placeholder="03XX-XXXXXXX"
                          maxLength="12"
                          disabled={isFamilyExisting}
                        />
                        {familyErrors.primaryContactMobile && (
                          <div
                            className="invalid-feedback"
                            style={{ fontSize: "11px" }}
                          >
                            {familyErrors.primaryContactMobile}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="mb-2">
                        <label
                          className="form-label fw-medium mb-1"
                          style={{ color: "#475569", fontSize: "13px" }}
                        >
                          Email
                        </label>
                        <input
                          id="family-email"
                          type="email"
                          className={`form-control ${
                            familyErrors.email ? "is-invalid" : ""
                          }`}
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            backgroundColor: isFamilyExisting
                              ? "#f8fafc"
                              : "#ffffff",
                          }}
                          value={familyInfo.email}
                          onChange={(e) =>
                            setFamilyInfo({
                              ...familyInfo,
                              email: e.target.value,
                            })
                          }
                          placeholder="Enter Email"
                          disabled={isFamilyExisting}
                        />
                        {familyErrors.email && (
                          <div
                            className="invalid-feedback"
                            style={{ fontSize: "11px" }}
                          >
                            {familyErrors.email}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="mb-2">
                        <label
                          className="form-label fw-medium mb-1"
                          style={{ color: "#475569", fontSize: "13px" }}
                        >
                          Job
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            backgroundColor: isFamilyExisting
                              ? "#f8fafc"
                              : "#ffffff",
                          }}
                          value={familyInfo.job}
                          onChange={(e) =>
                            setFamilyInfo({
                              ...familyInfo,
                              job: e.target.value,
                            })
                          }
                          placeholder="Enter Job"
                          disabled={isFamilyExisting}
                        />
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="mb-2">
                        <label
                          className="form-label fw-medium mb-1"
                          style={{ color: "#475569", fontSize: "13px" }}
                        >
                          Employer
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            backgroundColor: isFamilyExisting
                              ? "#f8fafc"
                              : "#ffffff",
                          }}
                          value={familyInfo.employerName}
                          onChange={(e) =>
                            setFamilyInfo({
                              ...familyInfo,
                              employerName: e.target.value,
                            })
                          }
                          placeholder="Employer Name"
                          disabled={isFamilyExisting}
                        />
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="mb-2">
                        <label
                          className="form-label fw-medium mb-1"
                          style={{ color: "#475569", fontSize: "13px" }}
                        >
                          Country
                        </label>
                        <select
                          className="form-select"
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            backgroundColor: isFamilyExisting
                              ? "#f8fafc"
                              : "#ffffff",
                          }}
                          value={familyInfo.countryId}
                          onChange={(e) =>
                            setFamilyInfo({
                              ...familyInfo,
                              countryId: e.target.value,
                              stateId: "",
                              cityId: "",
                            })
                          }
                          disabled={isFamilyExisting}
                        >
                          <option value="">Select Country</option>
                          {countries.map((country) => (
                            <option key={country.id} value={country.id}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="mb-2">
                        <label
                          className="form-label fw-medium mb-1"
                          style={{ color: "#475569", fontSize: "13px" }}
                        >
                          State
                        </label>
                        <select
                          className="form-select"
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            backgroundColor: isFamilyExisting
                              ? "#f8fafc"
                              : "#ffffff",
                          }}
                          value={familyInfo.stateId}
                          onChange={(e) =>
                            setFamilyInfo({
                              ...familyInfo,
                              stateId: e.target.value,
                              cityId: "",
                            })
                          }
                          disabled={isFamilyExisting}
                        >
                          <option value="">Select State</option>
                          {states.map((state) => (
                            <option key={state.id} value={state.id}>
                              {state.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="mb-2">
                        <label
                          className="form-label fw-medium mb-1"
                          style={{ color: "#475569", fontSize: "13px" }}
                        >
                          City
                        </label>
                        <select
                          className="form-select"
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            backgroundColor: isFamilyExisting
                              ? "#f8fafc"
                              : "#ffffff",
                          }}
                          value={familyInfo.cityId}
                          onChange={(e) =>
                            setFamilyInfo({
                              ...familyInfo,
                              cityId: e.target.value,
                            })
                          }
                          disabled={isFamilyExisting}
                        >
                          <option value="">Select City</option>
                          {cities.map((city) => (
                            <option key={city.id} value={city.id}>
                              {city.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-12 col-lg-12">
                      <div className="mb-2">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label
                            className="form-label fw-medium mb-0"
                            style={{ color: "#475569", fontSize: "13px" }}
                          >
                            Current Address
                          </label>
                          <div className="form-check m-0">
                            <input
                              className="form-check-input"
                              style={{ cursor: "pointer", width: "14px", height: "14px", marginTop: "2px" }}
                              type="checkbox"
                              id="copyAddress"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFamilyInfo((prev) => ({
                                    ...prev,
                                    permanentAddress: prev.currentAddress,
                                    mailingAddress: prev.currentAddress,
                                  }));
                                }
                              }}
                              disabled={isFamilyExisting}
                            />
                            <label
                              className="form-check-label ms-1"
                              htmlFor="copyAddress"
                              style={{ fontSize: "11px", color: "#64748b", cursor: "pointer", userSelect: "none" }}
                            >
                              Same for Permanent & Mailing
                            </label>
                          </div>
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            backgroundColor: isFamilyExisting
                              ? "#f8fafc"
                              : "#ffffff",
                          }}
                          value={familyInfo.currentAddress}
                          onChange={(e) =>
                            setFamilyInfo({
                              ...familyInfo,
                              currentAddress: e.target.value,
                            })
                          }
                          placeholder="Enter Current Address"
                          disabled={isFamilyExisting}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-2">
                        <label
                          className="form-label fw-medium mb-1"
                          style={{ color: "#475569", fontSize: "13px" }}
                        >
                          Permanent Address
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            backgroundColor: isFamilyExisting
                              ? "#f8fafc"
                              : "#ffffff",
                          }}
                          value={familyInfo.permanentAddress}
                          onChange={(e) =>
                            setFamilyInfo({
                              ...familyInfo,
                              permanentAddress: e.target.value,
                            })
                          }
                          placeholder="Enter Permanent Address"
                          disabled={isFamilyExisting}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-2">
                        <label
                          className="form-label fw-medium mb-1"
                          style={{ color: "#475569", fontSize: "13px" }}
                        >
                          Mailing Address
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          style={{
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                            backgroundColor: isFamilyExisting
                              ? "#f8fafc"
                              : "#ffffff",
                          }}
                          value={familyInfo.mailingAddress}
                          onChange={(e) =>
                            setFamilyInfo({
                              ...familyInfo,
                              mailingAddress: e.target.value,
                            })
                          }
                          placeholder="Enter Mailing Address"
                          disabled={isFamilyExisting}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Student Details Card - Compact Design */}
          <div className="col-12 mb-4" id="student-details-section">
            <div
              className="card shadow-sm border-0"
              style={{
                borderRadius: "10px",
                overflow: "hidden",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                className="card-header d-flex justify-content-between align-items-center py-2 px-3"
                style={{
                  backgroundColor: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      backgroundColor: "#10b981",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "10px",
                    }}
                  >
                    <i
                      className="fas fa-graduation-cap text-white"
                      style={{ fontSize: "14px" }}
                    ></i>
                  </div>
                  <div>
                    <h5
                      className="mb-0 fw-semibold"
                      style={{ color: "#1e293b", fontSize: "15px" }}
                    >
                      Student Details
                    </h5>
                    <small className="text-muted" style={{ fontSize: "11px" }}>
                      {students.length} student
                      {students.length !== 1 ? "s" : ""} to register
                      {selectedStudentId && (
                        <span
                          className="ms-2"
                          style={{ color: "#3b82f6", fontWeight: 500 }}
                        >
                          <i className="fas fa-pencil-alt me-1"></i>
                          Editing Registration #{selectedStudentId}
                        </span>
                      )}
                    </small>
                  </div>
                </div>
                <button
                  className="btn btn-outline-primary btn-sm d-flex align-items-center"
                  onClick={() =>
                    setIsStudentDetailsExpanded(!isStudentDetailsExpanded)
                  }
                  style={{
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    padding: "4px 10px",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  <i
                    className={`fas fa-${
                      isStudentDetailsExpanded ? "chevron-up" : "chevron-down"
                    } me-1`}
                    style={{ fontSize: "10px" }}
                  ></i>
                  {isStudentDetailsExpanded ? "Collapse" : "Expand"}
                </button>
              </div>

              {isStudentDetailsExpanded && (
                <div className="card-body" style={{ padding: "16px" }}>
                  {students.map((student, index) => (
                    <div key={index} className="mb-4">
                      {students.length > 1 && (
                        <div className="d-flex align-items-center mb-3">
                          <div
                            style={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "6px",
                              backgroundColor: "#3b82f6",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginRight: "10px",
                            }}
                          >
                            <span
                              className="text-white fw-bold"
                              style={{ fontSize: "12px" }}
                            >
                              {index + 1}
                            </span>
                          </div>
                          <h6
                            className="mb-0 fw-semibold"
                            style={{ color: "#1e293b", fontSize: "14px" }}
                          >
                            Student {index + 1}
                            {student.registrationId && (
                              <span
                                className="ms-2 text-primary"
                                style={{ fontSize: "12px" }}
                              >
                                (Editing Registration #{student.registrationId})
                              </span>
                            )}
                          </h6>
                        </div>
                      )}

                      <div className="row g-2">
                        <div className="col-md-6 col-lg-4">
                          <div className="mb-2">
                            <label
                              className="form-label fw-medium mb-1"
                              style={{ color: "#475569", fontSize: "13px" }}
                            >
                              Full Name <span className="text-danger">*</span>
                            </label>
                            <input
                              id={`student-${index}-studentFullName`}
                              type="text"
                              className={`form-control ${
                                studentErrors[index]?.studentFullName
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{
                                padding: "8px 12px",
                                borderRadius: "6px",
                                border: "1px solid #d1d5db",
                                fontSize: "13px",
                              }}
                              value={student.studentFullName}
                              onChange={(e) => {
                                const newStudents = [...students];
                                newStudents[index].studentFullName =
                                  e.target.value;
                                setStudents(newStudents);
                              }}
                              placeholder="Student Full Name"
                            />
                            {studentErrors[index]?.studentFullName && (
                              <div
                                className="invalid-feedback"
                                style={{ fontSize: "11px" }}
                              >
                                {studentErrors[index].studentFullName}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                          <div className="mb-2">
                            <label
                              className="form-label fw-medium mb-1"
                              style={{ color: "#475569", fontSize: "13px" }}
                            >
                              DOB <span className="text-danger">*</span>
                            </label>
                            <input
                              id={`student-${index}-dob`}
                              type="date"
                              className={`form-control ${
                                studentErrors[index]?.dob ? "is-invalid" : ""
                              }`}
                              style={{
                                padding: "8px 12px",
                                borderRadius: "6px",
                                border: "1px solid #d1d5db",
                                fontSize: "13px",
                              }}
                              value={
                                student.dob ? student.dob.split("T")[0] : ""
                              }
                              onChange={(e) => {
                                const selectedDate = new Date(e.target.value);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);

                                if (selectedDate > today) {
                                  const newStudentErrors = [...studentErrors];
                                  newStudentErrors[index] = {
                                    ...newStudentErrors[index],
                                    dob: "Future dates are not allowed",
                                  };
                                  setStudentErrors(newStudentErrors);

                                  const newStudents = [...students];
                                  newStudents[index].dob = "";
                                  setStudents(newStudents);
                                } else {
                                  const newStudents = [...students];
                                  newStudents[index].dob = e.target.value;
                                  setStudents(newStudents);

                                  const newStudentErrors = [...studentErrors];
                                  newStudentErrors[index] = {
                                    ...newStudentErrors[index],
                                    dob: "",
                                  };
                                  setStudentErrors(newStudentErrors);
                                }
                              }}
                              max={new Date().toISOString().split("T")[0]}
                            />
                            {studentErrors[index]?.dob && (
                              <div
                                className="invalid-feedback"
                                style={{ fontSize: "11px" }}
                              >
                                {studentErrors[index].dob}
                              </div>
                            )}
                            {student.dob && !studentErrors[index]?.dob && (
                              <small
                                className="text-success mt-1 d-block fw-medium"
                                style={{ fontSize: "11px" }}
                              >
                                <i className="fas fa-birthday-cake me-1"></i>
                                Age: {calculateAge(student.dob)}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                          <div className="mb-2">
                            <label
                              className="form-label fw-medium mb-1"
                              style={{ color: "#475569", fontSize: "13px" }}
                            >
                              Gender <span className="text-danger">*</span>
                            </label>
                            <select
                              id={`student-${index}-gender`}
                              className={`form-select ${
                                studentErrors[index]?.gender ? "is-invalid" : ""
                              }`}
                              style={{
                                padding: "8px 12px",
                                borderRadius: "6px",
                                border: "1px solid #d1d5db",
                                fontSize: "13px",
                              }}
                              value={student.gender}
                              onChange={(e) => {
                                const newStudents = [...students];
                                newStudents[index].gender = e.target.value;
                                setStudents(newStudents);
                              }}
                            >
                              <option value="">Select Gender</option>
                              {genders.map((gender) => (
                                <option key={gender.id} value={gender.name}>
                                  {gender.name}
                                </option>
                              ))}
                            </select>
                            {studentErrors[index]?.gender && (
                              <div
                                className="invalid-feedback"
                                style={{ fontSize: "11px" }}
                              >
                                {studentErrors[index].gender}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                          <div className="mb-2">
                            <label
                              className="form-label fw-medium mb-1"
                              style={{ color: "#475569", fontSize: "13px" }}
                            >
                              Language
                            </label>
                            <select
                              className="form-select"
                              style={{
                                padding: "8px 12px",
                                borderRadius: "6px",
                                border: "1px solid #d1d5db",
                                fontSize: "13px",
                              }}
                              value={student.language}
                              onChange={(e) => {
                                const newStudents = [...students];
                                newStudents[index].language = e.target.value;
                                setStudents(newStudents);
                              }}
                            >
                              <option value="">Select Language</option>
                              {homeLanguage.map((lang) => (
                                <option key={lang.id} value={lang.value}>
                                  {lang.value}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                          <div className="mb-2">
                            <label
                              className="form-label fw-medium mb-1"
                              style={{ color: "#475569", fontSize: "13px" }}
                            >
                              Class <span className="text-danger">*</span>
                            </label>
                            <select
                              id={`student-${index}-classId`}
                              className={`form-select ${
                                studentErrors[index]?.classId
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{
                                padding: "8px 12px",
                                borderRadius: "6px",
                                border: "1px solid #d1d5db",
                                fontSize: "13px",
                              }}
                              value={student.classId}
                              onChange={(e) => {
                                const classId = e.target.value;
                                const newStudents = [...students];
                                newStudents[index].classId = classId;
                                newStudents[index].className =
                                  classes.find(
                                    (c) => c.id === parseInt(classId),
                                  )?.name || "";
                                newStudents[index].feeElements = [];
                                setStudents(newStudents);
                                fetchFeePlan(classId, index);
                              }}
                            >
                              <option value="">Select Class</option>
                              {classes.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                            {studentErrors[index]?.classId && (
                              <div
                                className="invalid-feedback"
                                style={{ fontSize: "11px" }}
                              >
                                {studentErrors[index].classId}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                          <div className="mb-2">
                            <label
                              className="form-label fw-medium mb-1"
                              style={{ color: "#475569", fontSize: "13px" }}
                            >
                              Session <span className="text-danger">*</span>
                            </label>
                            <select
                              id={`student-${index}-applyingFor`}
                              className={`form-select ${
                                studentErrors[index]?.applyingFor
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{
                                padding: "8px 12px",
                                borderRadius: "6px",
                                border: "1px solid #d1d5db",
                                fontSize: "13px",
                                backgroundColor: "#f8fafc",
                              }}
                              value={student.applyingFor}
                              onChange={(e) => {
                                const newStudents = [...students];
                                newStudents[index].applyingFor = e.target.value;
                                setStudents(newStudents);
                              }}
                              disabled={true}
                            >
                              <option value="">Select Session</option>
                              {sessions.map((session) => (
                                <option
                                  key={session.id}
                                  value={session.sessionName}
                                >
                                  {session.sessionName}
                                </option>
                              ))}
                            </select>
                            {studentErrors[index]?.applyingFor && (
                              <div
                                className="invalid-feedback"
                                style={{ fontSize: "11px" }}
                              >
                                {studentErrors[index].applyingFor}
                              </div>
                            )}
                            {!student.applyingFor && (
                              <small
                                className="text-warning mt-1 d-block"
                                style={{ fontSize: "11px" }}
                              >
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                Contact organization to assign session
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                          <div className="mb-2">
                            <label
                              className="form-label fw-medium mb-1"
                              style={{ color: "#475569", fontSize: "13px" }}
                            >
                              Entry Term <span className="text-danger">*</span>
                            </label>
                            <select
                              id={`student-${index}-entryTerm`}
                              className={`form-select ${
                                studentErrors[index]?.entryTerm
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{
                                padding: "8px 12px",
                                borderRadius: "6px",
                                border: "1px solid #d1d5db",
                                fontSize: "13px",
                              }}
                              value={student.entryTerm}
                              onChange={(e) => {
                                const newStudents = [...students];
                                newStudents[index].entryTerm = e.target.value;
                                setStudents(newStudents);
                              }}
                            >
                              <option value="">Select Term</option>
                              {terms.map((term) => (
                                <option key={term.id} value={term.id}>
                                  {term.type}
                                </option>
                              ))}
                            </select>
                            {studentErrors[index]?.entryTerm && (
                              <div
                                className="invalid-feedback"
                                style={{ fontSize: "11px" }}
                              >
                                {studentErrors[index].entryTerm}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Concession dropdown moved to Manual Fee Section */}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Manual Fee Section Card */}
          <div className="col-12 mb-4" id="manual-fee-section">
            <div
              className="card shadow-sm border-0"
              style={{
                borderRadius: "10px",
                overflow: "hidden",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                className="card-header d-flex justify-content-between align-items-center py-2 px-3"
                style={{
                  backgroundColor: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      backgroundColor: "#3b82f6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "10px",
                    }}
                  >
                    <i
                      className="fas fa-money-bill-wave text-white"
                      style={{ fontSize: "14px" }}
                    ></i>
                  </div>
                  <div>
                    <h5
                      className="mb-0 fw-semibold"
                      style={{ color: "#1e293b", fontSize: "15px" }}
                    >
                      Manual Fee Section
                    </h5>
                    <small className="text-muted" style={{ fontSize: "11px" }}>
                      Manage fee elements and concessions for each student
                    </small>
                  </div>
                </div>
                <button
                  className="btn btn-outline-primary btn-sm d-flex align-items-center"
                  onClick={() => setIsManualFeeExpanded(!isManualFeeExpanded)}
                  style={{
                    borderRadius: "6px",
                    padding: "4px 10px",
                    fontSize: "12px",
                    transition: "all 0.2s",
                  }}
                >
                  <i
                    className={`fas fa-chevron-${isManualFeeExpanded ? "up" : "down"} me-1`}
                  ></i>
                  {isManualFeeExpanded ? "Collapse" : "Expand"}
                </button>
              </div>

              {isManualFeeExpanded && (
                <div className="card-body" style={{ padding: "16px" }}>
                {students.map((student, index) => (
                  <div key={index} className="mb-4 pb-4 border-bottom">
                    {students.length > 1 && (
                      <div className="d-flex align-items-center mb-3">
                        <div
                          style={{
                            width: "28px",
                            height: "28px",
                            borderRadius: "6px",
                            backgroundColor: "#3b82f6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginRight: "10px",
                          }}
                        >
                          <span
                            className="text-white fw-bold"
                            style={{ fontSize: "12px" }}
                          >
                            {index + 1}
                          </span>
                        </div>
                        <h6
                          className="mb-0 fw-semibold"
                          style={{ color: "#1e293b", fontSize: "14px" }}
                        >
                          Student {index + 1}: {student.studentFullName || "Unnamed"}
                        </h6>
                      </div>
                    )}

                    <div className="row mb-3">
                      <div className="col-md-6 col-lg-4">
                        {roleId === "3" && (
                          <div className="mb-2">
                            <label
                              className="form-label fw-medium mb-1"
                              style={{ color: "#475569", fontSize: "13px" }}
                            >
                              Concession
                            </label>
                            <select
                              id={`student-${index}-concession-fee`}
                              className="form-select"
                              style={{
                                padding: "8px 12px",
                                borderRadius: "6px",
                                border: "1px solid #d1d5db",
                                fontSize: "13px",
                              }}
                              value={student.concessionTypeId || ""}
                              onChange={(e) => handleConcessionChange(e, index)}
                            >
                              <option value="">Select Concession</option>
                              {concessionData.map((group, groupIdx) => (
                                <optgroup key={groupIdx} label={group.label}>
                                  {group.options.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 col-lg-8 d-flex align-items-center">
                        <h6 className="mb-0 fw-semibold text-primary">
                          Fee Plan: {student.feeElements?.length > 0 ? student.feeElements[0].feePlanName : "N/A"}
                        </h6>
                      </div>
                    </div>

                    <div className="row px-2">
                      <div className="col-12">
                        <div className="table-responsive rounded-3 border" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                          <table className="table table-hover align-middle mb-0" style={{ fontSize: "14px" }}>
                            <thead className="table-light" style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                              <tr>
                                <th className="fw-semibold text-secondary py-3 px-4" style={{ width: "30%" }}>Fee Elements</th>
                                <th className="fw-semibold text-secondary text-end py-3 px-4">Default Amount</th>
                                <th className="fw-semibold text-secondary text-center py-3 px-4" style={{ width: "20%" }}>Chargeable Amount</th>
                                <th className="fw-semibold text-secondary text-end py-3 px-4">Discount Given</th>
                                <th className="fw-semibold text-secondary text-center py-3 px-4">Print Default Amount on Challan</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white">
                              {student.feeElements?.length > 0 ? (
                                student.feeElements.map((fee, feeIndex) => (
                                  <tr key={feeIndex} className="border-bottom">
                                    <td className="py-3 px-4 text-dark fw-medium">{fee.feeElement}</td>
                                    <td className="py-3 px-4 text-end text-muted">{fee.defaultAmount}</td>
                                    <td className="py-3 px-4">
                                      <input
                                        type="number"
                                        className="form-control text-center shadow-sm"
                                        style={{ borderRadius: "6px", border: "1px solid #cbd5e1", transition: "all 0.2s" }}
                                        value={fee.chargebaleAmount}
                                        onChange={(e) => handleChargeableAmountChange(e, index, feeIndex)}
                                        disabled={!!student.concessionTypeId}
                                        onFocus={(e) => (e.target.value === '0' || e.target.value === '') && (e.target.value = '')}
                                      />
                                    </td>
                                    <td className="py-3 px-4 text-end">
                                      <span className="badge rounded-pill" style={{ backgroundColor: "#dcfce7", color: "#166534", padding: "6px 12px", fontSize: "13px", fontWeight: "600" }}>
                                        {fee.discount !== undefined ? fee.discount : (fee.defaultAmount - (fee.chargebaleAmount || 0))}
                                      </span>
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                      <div className="form-check d-flex justify-content-center m-0">
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          style={{ width: "18px", height: "18px", cursor: "pointer", borderColor: "#94a3b8" }}
                                          checked={!!fee.printDefaultFeeOnChallan}
                                          onChange={(e) => handlePrintDefaultFeeChange(e, index, feeIndex)}
                                        />
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="5" className="text-center text-muted py-5">
                                    <i className="fas fa-search-dollar fa-2x mb-3 text-light d-block" style={{ color: "#cbd5e1" }}></i>
                                    No fee elements found. Please select a class to view applicable fees.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          </div>

          {/* Document Attachments Card - Compact Fixed Design */}
          <div className="col-12 mb-4">
            <div
              className="card shadow-sm border-0"
              style={{
                borderRadius: "10px",
                overflow: "hidden",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                className="card-header d-flex justify-content-between align-items-center py-2 px-3"
                style={{
                  backgroundColor: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      backgroundColor: "#f59e0b",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "10px",
                    }}
                  >
                    <i
                      className="fas fa-paperclip text-white"
                      style={{ fontSize: "14px" }}
                    ></i>
                  </div>
                  <div>
                    <h5
                      className="mb-0 fw-semibold"
                      style={{ color: "#1e293b", fontSize: "15px" }}
                    >
                      Document Attachments
                    </h5>
                    <small className="text-muted" style={{ fontSize: "11px" }}>
                      Upload required documents for each student
                    </small>
                  </div>
                </div>
                <button
                  className="btn btn-outline-primary btn-sm d-flex align-items-center"
                  onClick={() =>
                    setIsAttachmentsExpanded(!isAttachmentsExpanded)
                  }
                  style={{
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    padding: "4px 10px",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  <i
                    className={`fas fa-${
                      isAttachmentsExpanded ? "chevron-up" : "chevron-down"
                    } me-1`}
                    style={{ fontSize: "10px" }}
                  ></i>
                  {isAttachmentsExpanded ? "Collapse" : "Expand"}
                </button>
              </div>

              {isAttachmentsExpanded && (
                <div className="card-body" style={{ padding: "16px" }}>
                  {students.map((student, studentIndex) => (
                    <div key={studentIndex} className="mb-4">
                      {students.length > 1 && (
                        <div className="d-flex align-items-center mb-3">
                          <div
                            style={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "6px",
                              backgroundColor: "#f59e0b",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginRight: "10px",
                            }}
                          >
                            <span
                              className="text-white fw-bold"
                              style={{ fontSize: "12px" }}
                            >
                              {studentIndex + 1}
                            </span>
                          </div>
                          <h6
                            className="mb-0 fw-semibold"
                            style={{ color: "#1e293b", fontSize: "14px" }}
                          >
                            Student {studentIndex + 1}:{" "}
                            {student.studentFullName || "Unnamed Student"}
                          </h6>
                        </div>
                      )}

                      <div className="mb-3">
                        <div className="d-flex align-items-center mb-2">
                          <i
                            className="fas fa-info-circle me-2"
                            style={{ color: "#3b82f6", fontSize: "14px" }}
                          ></i>
                          <h6
                            className="mb-0 fw-semibold"
                            style={{ color: "#475569", fontSize: "13px" }}
                          >
                            Upload Birth Certificate, B-Form, and CNIC
                          </h6>
                        </div>

                        {/* Upload Section - Compact with button only */}
                        <div className="mb-3">
                          <div className="d-flex align-items-center gap-2">
                            <input
                              type="file"
                              className="form-control d-none"
                              accept=".pdf,.jpg,.png"
                              id={`file-upload-${studentIndex}`}
                              ref={fileInputRefs[studentIndex]}
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleFileChange(
                                    studentIndex,
                                    e.target.files[0],
                                  );
                                }
                              }}
                            />
                            <label
                              htmlFor={`file-upload-${studentIndex}`}
                              className="btn btn-primary btn-sm d-flex align-items-center"
                              style={{
                                borderRadius: "6px",
                                padding: "6px 12px",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: 500,
                              }}
                            >
                              <i
                                className="fas fa-upload me-2"
                                style={{ fontSize: "12px" }}
                              ></i>
                              Choose File
                            </label>
                            <span
                              className="text-muted"
                              style={{ fontSize: "12px" }}
                            >
                              Max 5MB per file (PDF, JPG, PNG)
                            </span>
                          </div>
                        </div>

                        {/* Uploaded Files List - Compact */}
                        {attachments[studentIndex]?.length > 0 && (
                          <div className="mt-3">
                            <div className="d-flex align-items-center mb-2">
                              <i
                                className="fas fa-folder-open me-2"
                                style={{ color: "#10b981", fontSize: "14px" }}
                              ></i>
                              <h6
                                className="mb-0 fw-semibold"
                                style={{ color: "#475569", fontSize: "13px" }}
                              >
                                Uploaded Documents (
                                {attachments[studentIndex].length})
                              </h6>
                            </div>
                            <div className="row g-2">
                              {attachments[studentIndex].map(
                                (file, fileIndex) => (
                                  <div
                                    key={fileIndex}
                                    className="col-12 col-md-6"
                                  >
                                    <div
                                      className="card border-0"
                                      style={{
                                        backgroundColor: "#ffffff",
                                        borderRadius: "8px",
                                        border: "1px solid #e2e8f0",
                                        overflow: "hidden",
                                      }}
                                    >
                                      <div className="card-body p-2">
                                        <div className="d-flex align-items-center justify-content-between">
                                          <div
                                            className="d-flex align-items-center"
                                            style={{ flex: 1 }}
                                          >
                                            <div
                                              style={{
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "6px",
                                                backgroundColor: "#dbeafe",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                marginRight: "10px",
                                              }}
                                            >
                                              <i
                                                className="fas fa-file-alt"
                                                style={{
                                                  color: "#3b82f6",
                                                  fontSize: "14px",
                                                }}
                                              ></i>
                                            </div>
                                            <div
                                              style={{ flex: 1, minWidth: 0 }}
                                            >
                                              <div
                                                className="text-truncate fw-medium"
                                                style={{
                                                  color: "#1e293b",
                                                  fontSize: "12px",
                                                  marginBottom: "2px",
                                                }}
                                              >
                                                {file.name}
                                              </div>
                                              <div
                                                className="text-muted"
                                                style={{ fontSize: "11px" }}
                                              >
                                                {(
                                                  file.size /
                                                  1024 /
                                                  1024
                                                ).toFixed(2)}{" "}
                                                MB
                                              </div>
                                            </div>
                                          </div>
                                          <button
                                            className="btn btn-sm"
                                            onClick={() =>
                                              handleRemoveFile(
                                                studentIndex,
                                                fileIndex,
                                              )
                                            }
                                            title="Remove file"
                                            style={{
                                              backgroundColor: "transparent",
                                              border: "1px solid #fecaca",
                                              color: "#ef4444",
                                              borderRadius: "4px",
                                              padding: "4px 8px",
                                              fontSize: "11px",
                                              marginLeft: "8px",
                                            }}
                                          >
                                            <i className="fas fa-trash"></i>
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons - Compact Design */}
          <div className="col-12 mt-4">
            <div className="d-flex justify-content-end gap-2">
              <button
                className="btn"
                onClick={handleSave}
                disabled={loading}
                style={{
                  backgroundColor: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 20px",
                  fontSize: "14px",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <i
                  className="fas fa-save me-2"
                  style={{ fontSize: "13px" }}
                ></i>
                Save
              </button>
              <button
                className="btn"
                onClick={handlePrint}
                disabled={loading}
                style={{
                  backgroundColor: "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 20px",
                  fontSize: "14px",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <i
                  className="fas fa-print me-2"
                  style={{ fontSize: "13px" }}
                ></i>
                Print
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentInquiry;